/**
 * JoinBuilder - Build JOIN operations for views
 *
 * Supports joining with both datasets and views with proper dependency tracking
 * to prevent circular references and deep dependency chains.
 *
 * Column condition fields use Select pick lists populated from the
 * preview-columns API (Monday Item ID: 11501067354).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, AlertCircle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SourceTypeSelector } from './SourceTypeSelector';
import { ViewSelector } from './ViewSelector';
import { DatasetCombobox } from '@/components/datasets/DatasetCombobox';
import { usePreviewColumns } from '@/hooks/useViews';
import { PerformanceIndicator } from '../PerformanceIndicator';
import { JoinTypeHelp } from '../HelpTooltips';
import { calculateChainDepth, hasViewJoins as checkHasViewJoins } from '@/lib/utils/viewPerformance';
import type { JoinConfig, JoinCondition, ViewRule, ColumnSourceInfo } from '@/types/views';
// The catalog `Dataset` (not the upload-flow one): `DatasetCombobox` hands back
// the row it fetched from the dataset list endpoint.
import type { Dataset } from '@/types';

export interface JoinBuilderProps {
  joins: JoinConfig[];
  onUpdate: (joins: JoinConfig[]) => void;
  currentViewId?: string;
  disabled?: boolean;
  /** File selection rules from the view being built — needed to fetch available columns. */
  rules?: ViewRule[];
  /** Rule logic operator. Defaults to 'AND'. */
  ruleLogic?: 'AND' | 'OR';
}

// Join type options
const JOIN_TYPES = [
  { value: 'INNER', label: 'INNER JOIN' },
  { value: 'LEFT', label: 'LEFT JOIN' },
  { value: 'RIGHT', label: 'RIGHT JOIN' },
  { value: 'FULL', label: 'FULL OUTER JOIN' },
] as const;

export const JoinBuilder: React.FC<JoinBuilderProps> = ({
  joins,
  onUpdate,
  currentViewId,
  disabled = false,
  rules = [],
  ruleLogic = 'AND',
}) => {
  // Column preview state
  const previewColumns = usePreviewColumns();
  const [columnSourceInfo, setColumnSourceInfo] = useState<ColumnSourceInfo | null>(null);
  const [columnFetchError, setColumnFetchError] = useState(false);
  const fetchRequestRef = useRef(0);

  /**
   * Memoize join target identifiers so the effect only re-fires when targets actually change.
   */
  const joinTargetIds = useMemo(
    () => joins.map((j) => j.target_view_id || j.target_dataset_id || '').join(','),
    [joins]
  );

  /**
   * Reset column state synchronously (as a guarded render-time adjustment,
   * not an effect) whenever rules/ruleLogic/joinTargetIds change and there
   * are no rules — mirrors the previous values in state so the reset only
   * fires on an actual change, converging in one extra render. Matches the
   * early-return branch the fetch effect below used to run synchronously.
   */
  const [prevJoinRules, setPrevJoinRules] = useState(rules);
  const [prevJoinRuleLogic, setPrevJoinRuleLogic] = useState(ruleLogic);
  const [prevJoinTargetIds, setPrevJoinTargetIds] = useState(joinTargetIds);
  if (
    rules !== prevJoinRules ||
    ruleLogic !== prevJoinRuleLogic ||
    joinTargetIds !== prevJoinTargetIds
  ) {
    setPrevJoinRules(rules);
    setPrevJoinRuleLogic(ruleLogic);
    setPrevJoinTargetIds(joinTargetIds);
    if (rules.length === 0) {
      setColumnSourceInfo(null);
      setColumnFetchError(false);
    }
  }

  /**
   * Fetch columns whenever rules, ruleLogic, or join targets change.
   */
  useEffect(() => {
    if (rules.length === 0) {
      return;
    }

    const requestId = ++fetchRequestRef.current;

    const fetchColumns = async () => {
      try {
        setColumnFetchError(false);
        const joinsWithTargets = joins
          .filter((j) => j.target_view_id || j.target_dataset_id)
          .map(({ on_conditions: _conds, ...rest }) => ({ ...rest, on_conditions: [] }));
        const result = await previewColumns.mutateAsync({
          rules,
          rule_logic: ruleLogic,
          joins: joinsWithTargets.length > 0 ? joinsWithTargets : undefined,
        });
        // Only apply if this is still the latest request
        if (fetchRequestRef.current === requestId) {
          setColumnSourceInfo(result.source_info);
        }
      } catch {
        if (fetchRequestRef.current === requestId) {
          setColumnFetchError(true);
          setColumnSourceInfo(null);
        }
      }
    };

    fetchColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, ruleLogic, joinTargetIds]);

  /** Base columns from the view being built */
  const baseColumns = columnSourceInfo?.base_columns ?? [];

  /** Look up right-column options for a specific join target */
  const getRightColumns = useCallback(
    (join: JoinConfig): string[] => {
      if (!columnSourceInfo?.joined_columns) return [];
      const targetName = join.target_view_name || '';
      return columnSourceInfo.joined_columns[targetName] ?? [];
    },
    [columnSourceInfo]
  );

  /**
   * Determine the placeholder text for a column Select based on current state.
   */
  const getColumnPlaceholder = (
    join: JoinConfig,
    side: 'left' | 'right'
  ): string => {
    if (rules.length === 0) return 'Configure data source first';
    if (!join.target_view_id && !join.target_dataset_id) return 'Select a source first';
    if (columnFetchError) return 'Could not load columns';
    if (previewColumns.isPending) return 'Loading columns\u2026';
    if (side === 'left' && baseColumns.length === 0) return 'No columns available';
    return 'Select column';
  };

  /**
   * Get all left-side column options for a given join index.
   * Includes base columns plus columns from all previously-joined sources.
   */
  const getLeftColumnOptions = useCallback(
    (joinIndex: number): { value: string; label: string; source?: string }[] => {
      const options: { value: string; label: string; source?: string }[] = [];

      // Base columns
      for (const col of baseColumns) {
        options.push({ value: col, label: col });
      }

      // Columns from previously-joined sources (indices 0..joinIndex-1)
      if (columnSourceInfo?.joined_columns) {
        for (let i = 0; i < joinIndex; i++) {
          const priorJoin = joins[i];
          const sourceName = priorJoin.target_view_name || '';
          const cols = columnSourceInfo.joined_columns[sourceName] ?? [];
          for (const col of cols) {
            options.push({
              value: `${sourceName}.${col}`,
              label: `${sourceName}.${col}`,
              source: sourceName,
            });
          }
        }
      }

      return options;
    },
    [baseColumns, columnSourceInfo, joins]
  );

  /**
   * Whether a column Select should be disabled.
   */
  const isColumnSelectDisabled = (
    join: JoinConfig,
    side?: 'left' | 'right',
    joinIndex?: number,
  ): boolean => {
    if (disabled) return true;
    if (rules.length === 0) return true;
    if (!join.target_view_id && !join.target_dataset_id) return true;
    if (columnFetchError) return true;
    if (previewColumns.isPending) return true;
    if (side === 'left') {
      const leftOpts = getLeftColumnOptions(joinIndex ?? 0);
      if (leftOpts.length === 0) return true;
    }
    return false;
  };

  /**
   * Build the option list, including a saved value that is no longer present.
   */
  const buildOptions = (
    availableColumns: string[],
    currentValue: string
  ): { value: string; label: string }[] => {
    const options = availableColumns.map((col) => ({ value: col, label: col }));
    if (
      currentValue &&
      !availableColumns.includes(currentValue)
    ) {
      options.unshift({
        value: currentValue,
        label: `${currentValue} (saved)`,
      });
    }
    return options;
  };

  /**
   * Calculate excluded view IDs to prevent circular dependencies
   */
  const excludedViewIds = useMemo(() => {
    const excluded: string[] = [];

    // Always exclude the current view being edited/created
    if (currentViewId) {
      excluded.push(currentViewId);
    }

    // Exclude any views already in the join chain to prevent duplicates
    joins.forEach((join) => {
      if (join.source_type === 'view' && join.target_view_id) {
        excluded.push(join.target_view_id);
      }
    });

    return excluded;
  }, [currentViewId, joins]);

  /**
   * Add a new JOIN configuration
   */
  const addJoin = () => {
    const newJoin: JoinConfig = {
      id: `join-${Date.now()}`,
      type: 'INNER',
      source_type: 'view',
      target_view_id: '',
      target_view_name: '',
      on_conditions: [
        {
          id: `join-cond-${Date.now()}`,
          left_column: '',
          right_column: '',
          logic: 'AND',
        },
      ],
    };
    onUpdate([...joins, newJoin]);
  };

  /**
   * Update a JOIN configuration
   */
  const updateJoin = (id: string, updates: Partial<JoinConfig>) => {
    onUpdate(
      joins.map((join) =>
        join.id === id ? { ...join, ...updates } : join
      )
    );
  };

  /**
   * Remove a JOIN configuration
   */
  const removeJoin = (id: string) => {
    onUpdate(joins.filter((join) => join.id !== id));
  };

  /**
   * Add a new join condition to a JOIN
   */
  const addJoinCondition = (joinId: string) => {
    const newCondition: JoinCondition = {
      id: `join-cond-${Date.now()}`,
      left_column: '',
      right_column: '',
      logic: 'AND',
    };
    onUpdate(
      joins.map((join) =>
        join.id === joinId
          ? { ...join, on_conditions: [...join.on_conditions, newCondition] }
          : join
      )
    );
  };

  /**
   * Update a join condition
   */
  const updateJoinCondition = (
    joinId: string,
    conditionId: string,
    updates: Partial<JoinCondition>
  ) => {
    onUpdate(
      joins.map((join) =>
        join.id === joinId
          ? {
              ...join,
              on_conditions: join.on_conditions.map((cond) =>
                cond.id === conditionId ? { ...cond, ...updates } : cond
              ),
            }
          : join
      )
    );
  };

  /**
   * Remove a join condition
   */
  const removeJoinCondition = (joinId: string, conditionId: string) => {
    onUpdate(
      joins.map((join) =>
        join.id === joinId
          ? {
              ...join,
              on_conditions: join.on_conditions.filter(
                (cond) => cond.id !== conditionId
              ),
            }
          : join
      )
    );
  };

  /**
   * Handle source type change - clear target and condition columns when switching
   */
  const handleSourceTypeChange = (joinId: string, sourceType: 'dataset' | 'view') => {
    const join = joins.find((j) => j.id === joinId);
    const clearedConditions = join
      ? join.on_conditions.map((c) => ({ ...c, left_column: '', right_column: '' }))
      : undefined;
    updateJoin(joinId, {
      source_type: sourceType,
      target_view_id: '',
      target_view_name: '',
      target_dataset_id: '',
      ...(clearedConditions ? { on_conditions: clearedConditions } : {}),
    });
  };

  /**
   * Handle dataset selection — clear condition columns when target changes
   */
  const handleDatasetSelect = (joinId: string, dataset: Dataset) => {
    const join = joins.find((j) => j.id === joinId);
    const targetChanged =
      join && (join.target_dataset_id !== dataset.name || join.target_view_name !== dataset.name);
    const clearedConditions =
      targetChanged && join
        ? join.on_conditions.map((c) => ({ ...c, left_column: '', right_column: '' }))
        : undefined;

    onUpdate(
      joins.map((j) =>
        j.id === joinId
          ? {
              ...j,
              target_dataset_id: dataset.name,
              target_view_id: dataset.name,
              target_view_name: dataset.name,
              ...(clearedConditions ? { on_conditions: clearedConditions } : {}),
            }
          : j
      )
    );
  };

  /**
   * Handle view selection — clear condition columns when target changes
   */
  const handleViewSelect = (joinId: string, view: any) => {
    const join = joins.find((j) => j.id === joinId);
    const targetChanged = join && join.target_view_id !== view.id;
    const clearedConditions =
      targetChanged && join
        ? join.on_conditions.map((c) => ({ ...c, left_column: '', right_column: '' }))
        : undefined;

    onUpdate(
      joins.map((j) =>
        j.id === joinId
          ? {
              ...j,
              target_view_id: view.id,
              target_view_name: view.name,
              ...(clearedConditions ? { on_conditions: clearedConditions } : {}),
            }
          : j
      )
    );
  };

  /**
   * Validate if a join is complete
   */
  const isJoinValid = (join: JoinConfig): boolean => {
    const hasTarget = !!(join.target_view_id || join.target_dataset_id);
    const hasValidConditions = join.on_conditions.every(
      (cond) => cond.left_column && cond.right_column
    );
    return hasTarget && hasValidConditions;
  };

  /**
   * Calculate performance metrics
   */
  const performanceMetrics = useMemo(() => {
    const chainDepth = calculateChainDepth(joins);
    const hasViews = checkHasViewJoins(joins);
    return {
      chainDepth,
      joinCount: joins.length,
      hasViewJoins: hasViews,
    };
  }, [joins]);

  return (
    <div className="space-y-4">
      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Join this view with datasets or other views. Select a source type, choose a target,
          and define the join conditions (column mappings).
        </AlertDescription>
      </Alert>

      {/* Performance Indicator - show when JOINs exist */}
      {joins.length > 0 && (
        <PerformanceIndicator
          {...performanceMetrics}
          showCompact={true}
        />
      )}

      {/* Add JOIN button */}
      <Button onClick={addJoin} size="sm" variant="outline" disabled={disabled}>
        <Plus className="h-4 w-4 mr-2" />
        Add JOIN
      </Button>

      {/* JOINs list */}
      {joins.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
          No JOINs configured. Click &quot;Add JOIN&quot; to combine with datasets or views.
        </div>
      ) : (
        <div className="space-y-4">
          {joins.map((join, joinIndex) => {
            const sourceType = join.source_type || 'view';
            const isValid = isJoinValid(join);
            const leftDisabled = isColumnSelectDisabled(join, 'left', joinIndex);
            const rightDisabled = isColumnSelectDisabled(join, 'right', joinIndex);
            const rightColumns = getRightColumns(join);

            return (
              <Card key={join.id} className="p-4 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">JOIN {joinIndex + 1}</span>
                    {!isValid && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                  <Button
                    onClick={() => removeJoin(join.id)}
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    disabled={disabled}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {/* Source Type Selector */}
                <SourceTypeSelector
                  value={sourceType}
                  onChange={(type) => handleSourceTypeChange(join.id, type)}
                  disabled={disabled}
                />

                {/* Join Type */}
                <div>
                  <Label htmlFor={`join-type-${join.id}`} className="flex items-center gap-2">
                    Join Type
                    <JoinTypeHelp />
                  </Label>
                  <Select
                    value={join.type}
                    onValueChange={(value) =>
                      updateJoin(join.id, { type: value as JoinConfig['type'] })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger id={`join-type-${join.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {JOIN_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Target Selection */}
                {sourceType === 'dataset' ? (
                  <div>
                    <Label htmlFor={`join-dataset-${join.id}`}>Target Dataset</Label>
                    <DatasetCombobox
                      id={`join-dataset-${join.id}`}
                      value={join.target_dataset_id || null}
                      valueField="name"
                      placeholder="Select a dataset"
                      disabled={disabled}
                      onChange={(_value, dataset) => {
                        if (dataset) {
                          handleDatasetSelect(join.id, dataset);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <ViewSelector
                    selectedViewId={join.target_view_id}
                    onSelect={(view) => handleViewSelect(join.id, view)}
                    excludeViewIds={excludedViewIds}
                    showDependencyWarnings={true}
                    disabled={disabled}
                  />
                )}

                {/* Join Conditions */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Join Conditions (ON)</Label>
                    <Button
                      onClick={() => addJoinCondition(join.id)}
                      size="sm"
                      variant="outline"
                      disabled={disabled}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Condition
                    </Button>
                  </div>

                  {join.on_conditions.map((condition, condIndex) => {
                    const leftColumnOptions = getLeftColumnOptions(joinIndex);
                    const leftSelectValue = condition.left_source
                      ? `${condition.left_source}.${condition.left_column}`
                      : condition.left_column;
                    const leftOptions = buildOptions(
                      leftColumnOptions.map((o) => o.value),
                      leftSelectValue
                    );
                    const rightOptions = buildOptions(rightColumns, condition.right_column);

                    return (
                      <div
                        key={condition.id}
                        className="border rounded-lg p-3 bg-muted/50 space-y-2"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Left Column Select */}
                          <div>
                            <Label
                              htmlFor={`join-left-${condition.id}`}
                              className="text-xs"
                            >
                              Left Column
                            </Label>
                            <Select
                              value={leftSelectValue || ''}
                              onValueChange={(value) => {
                                const match = leftColumnOptions.find((o) => o.value === value);
                                if (match?.source) {
                                  const colName = value.substring(match.source.length + 1);
                                  updateJoinCondition(join.id, condition.id, {
                                    left_column: colName,
                                    left_source: match.source,
                                  });
                                } else {
                                  updateJoinCondition(join.id, condition.id, {
                                    left_column: value,
                                    left_source: undefined,
                                  });
                                }
                              }}
                              disabled={leftDisabled}
                            >
                              <SelectTrigger id={`join-left-${condition.id}`} className="h-9">
                                <SelectValue placeholder={getColumnPlaceholder(join, 'left')} />
                              </SelectTrigger>
                              <SelectContent>
                                {leftOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Right Column Select */}
                          <div>
                            <Label
                              htmlFor={`join-right-${condition.id}`}
                              className="text-xs"
                            >
                              Right Column (Target {sourceType === 'dataset' ? 'Dataset' : 'View'})
                            </Label>
                            <Select
                              value={condition.right_column || ''}
                              onValueChange={(value) =>
                                updateJoinCondition(join.id, condition.id, {
                                  right_column: value,
                                })
                              }
                              disabled={rightDisabled}
                            >
                              <SelectTrigger id={`join-right-${condition.id}`} className="h-9">
                                <SelectValue placeholder={getColumnPlaceholder(join, 'right')} />
                              </SelectTrigger>
                              <SelectContent>
                                {rightOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          {condIndex < join.on_conditions.length - 1 && (
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Logic:</Label>
                              <Select
                                value={condition.logic || 'AND'}
                                onValueChange={(value) =>
                                  updateJoinCondition(join.id, condition.id, {
                                    logic: value as 'AND' | 'OR',
                                  })
                                }
                                disabled={disabled}
                              >
                                <SelectTrigger className="w-20 h-7">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="AND">AND</SelectItem>
                                  <SelectItem value="OR">OR</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <Button
                            onClick={() => removeJoinCondition(join.id, condition.id)}
                            size="sm"
                            variant="ghost"
                            className="h-7 ml-auto"
                            disabled={disabled || join.on_conditions.length === 1}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Validation warning */}
                {!isValid && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      Please select a target {sourceType} and define at least one complete
                      join condition.
                    </AlertDescription>
                  </Alert>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
